// Seeded deterministic PRNG. The sim core NEVER calls Math.random() or
// reads a wall clock — all randomness comes from named, independently-seeded
// sub-streams so a save + command log reproduce identical state.
//
// mulberry32: tiny, fast, good-enough distribution for gameplay (not crypto).

export interface Rng {
  /** float in [0, 1) */
  next(): number
  /** float in [min, max) */
  range(min: number, max: number): number
}

export function makeRng(seed: number): Rng {
  let a = seed >>> 0
  const next = (): number => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  return {
    next,
    range: (min, max) => min + next() * (max - min),
  }
}

// --- Serializable form -------------------------------------------------------
// The closure `a` in makeRng is invisible to serialization. The sim core keeps
// its PRNG state on SimState instead, so a saved or snapshotted state
// reproduces the stream exactly. `RngState` is a plain number holder;
// nextRng/rangeRng advance it in place.

/** A serializable mulberry32 cursor. `a` is the running state. */
export interface RngState {
  a: number
}

/** Seed a fresh serializable sub-stream cursor. */
export function makeRngState(seed: number): RngState {
  return { a: seed >>> 0 }
}

/** Advance the cursor and return a float in [0, 1). Mutates `state.a`. */
export function nextRng(state: RngState): number {
  state.a |= 0
  state.a = (state.a + 0x6d2b79f5) | 0
  let t = Math.imul(state.a ^ (state.a >>> 15), 1 | state.a)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

/** Advance the cursor and return a float in [min, max). Mutates `state.a`. */
export function rangeRng(state: RngState, min: number, max: number): number {
  return min + nextRng(state) * (max - min)
}

/**
 * Derive an independent sub-stream seed from a master seed and a stable name
 * (e.g. "map", "traits"). Named streams never share state.
 */
export function subSeed(master: number, name: string): number {
  let h = master >>> 0
  for (let i = 0; i < name.length; i++) {
    h = Math.imul(h ^ name.charCodeAt(i), 0x01000193) >>> 0
  }
  return h >>> 0
}
